import { Divider } from "@mui/material";
import { useTheme } from "../../../components/hooks/useTheme";
import { useEffect, useState } from "react";
import { bookingsStatusColorCode } from "../../../utils/statusColorCode";
import { format } from "date-fns";
import SecuityDeposit from "./SecuityDeposit2";
import BasePriceRateChange from "./BasePriceRateChange";


type SummaryProps = {
  currentPage: number;
  reservationId: number;
  summaryData: any;
  showDateAndTimeBox: () => void;
  selectedVehicleClass: any;
  securityDepositAmount: number;
  securityDepositAmountPaid: number;
  updateSecurityDepositAmountToPaid: (updateAmount: number) => void;
  setWasSecurityDepositManuallyChanged: (value: boolean) => void;
  refreshTheSummary: () => void;
  setPleaseWaitPopup: (value: boolean) => void;
  onRateChangeBeforeBookingConfirm: (type: string, amount: number) => void;
};

export const Summary = ({
  currentPage,
  reservationId,
  summaryData,
  showDateAndTimeBox,
  selectedVehicleClass,
  securityDepositAmount,
  updateSecurityDepositAmountToPaid,
  setWasSecurityDepositManuallyChanged,
  refreshTheSummary,
  setPleaseWaitPopup,
  onRateChangeBeforeBookingConfirm
}: SummaryProps) => {
  const { themeStyles } = useTheme();
  const [statusBg, setStatusBg] = useState<string>("");
  const [statusTextColor, setStatusTextColor] = useState<string>("");
  const [openSecurityDepositDialog, setOpenSecurityDepositDialog] = useState<boolean>(false);
  const [openBasePriceRateDialog, setOpenBasePriceRateDialog] = useState<boolean>(false);
  const [basePriceType, setBasePriceType] = useState<string>("Hourly");

  useEffect(() => {
    let color = {
      colorBg: "#000000",
      colorText: "#000000"
    };

    color = bookingsStatusColorCode?.find((color: any) => color?.label === summaryData?.status.trim()?.toLowerCase()) || {
      colorBg: "#000000",
      colorText: "#000000"
    };

    setStatusBg(color.colorBg);
    setStatusTextColor(color.colorText);
  }, [summaryData])

  return (
    <>
      <div className={`w-full bg-transparent p-2 flex flex-col gap-1.5 rounded-lg shadow-sm border mt-4 ${themeStyles.main.borderColor}`}>
        <div className={`flex justify-between items-center py-4 border-b ${themeStyles.main.borderColor}`}>
          <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>Summary</span>

          <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${statusBg} ${statusTextColor} border ${themeStyles.main.borderColor}`}>
            {summaryData?.status}
          </span>
        </div>

        <div className="flex flex-col gap-1.5 py-2">
          <div className="flex flex-col justify-between items-start mb-4">
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textSubHeadingColor}`}>Branch</span>
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.branchName}</span>
          </div>

          <div className="flex flex-col justify-between items-start">
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textSubHeadingColor}`}>Initial Pickup Date
              <span
                className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                onClick={() => showDateAndTimeBox()}
              >
                edit
              </span>
            </span>
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{format(summaryData?.pickupDate ? summaryData?.pickupDate : new Date(), "EEEE, MMMM dd, yyyy @ hh:mm a")}</span>
          </div>

          <div className="flex flex-col justify-between items-start mb-4">
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textSubHeadingColor}`}>Pickup Location</span>
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.pickupLocationName}</span>
          </div>

          <div className="flex flex-col justify-between items-start">
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textSubHeadingColor}`}>Initial Return Date
              <span
                className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                onClick={() => showDateAndTimeBox()}
              >
                edit
              </span>
            </span>
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{format(summaryData?.returnDate ? summaryData?.returnDate : new Date(), "EEEE, MMMM dd, yyyy @ hh:mm a")}</span>
          </div>

          <div className="flex flex-col justify-between items-start mb-4">
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textSubHeadingColor}`}>Return Location</span>
            <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.returnLocationName}</span>
          </div>

          {
            currentPage !== 1 && (
              <div className="flex flex-col mb-4">
                <span className={`text-xs font-medium break-all text-left ${themeStyles.main.textSubHeadingColor}`}>Vehicle Class</span>
                <span className={`text-xs font-medium break-all w-full text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.vehicleClassLabel}</span>

                {
                  (summaryData?.status === "Quotes" || summaryData?.status === "Open") &&
                  <span className={`text-xs font-medium break-all w-full text-left text-red-500`}>{selectedVehicleClass?.availableVehicles < 0 ? "(Not Available)" : ""}</span>
                }
              </div>
            )
          }

          <div className="flex justify-between gap-x-[0.25rem]">
            {
              currentPage !== 1 && (
                <div className="flex-1 flex flex-col justify-between items-start">
                  <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>Included Distance</span>
                  <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textHeadingColor}`}>{`${summaryData?.includedMiles} ${summaryData?.includedMiles > 1 ? "miles" : "mile"}`}</span>
                </div>
              )
            }
          </div>
        </div>

        {
          currentPage !== 1 && (
            <>
              <Divider className={`w-full ${themeStyles.divider.bg}`} />

              <div className={`pt-2`}>
                <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>Price Details</span>
              </div>

              <div className="flex flex-col gap-1.5">
                {
                  summaryData?.totalMonths !== 0 && (
                    <div className="flex justify-between items-start w-full">
                      <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.totalMonths}x Months</span>

                      <div className="flex items-center gap-x-[0.125rem]">
                        <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}> ${summaryData?.monthlyRate?.toFixed(2)}</span>
                        <span
                          className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                          onClick={() => {
                            setBasePriceType("Monthly");
                            setOpenBasePriceRateDialog(true);
                          }}
                        >
                          edit
                        </span>
                      </div>

                      <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${(summaryData?.totalMonths * summaryData?.monthlyRate)?.toFixed(2)}</span>
                    </div>
                  )
                }

                {
                  summaryData?.totalWeeks !== 0 && (
                    <div className="flex justify-between items-start w-full">
                      <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.totalWeeks}x Weeks</span>

                      <div className="flex items-center gap-x-[0.125rem]">
                        <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${summaryData?.weeklyRate?.toFixed(2)}</span>
                        <span
                          className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                          onClick={() => {
                            setBasePriceType("Weekly");
                            setOpenBasePriceRateDialog(true);
                          }}
                        >
                          edit
                        </span>
                      </div>

                      <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${(summaryData?.totalWeeks * summaryData?.weeklyRate)?.toFixed(2)}</span>
                    </div>
                  )
                }

                {
                  summaryData?.totalDays !== 0 && (
                    <div className="flex justify-between items-start w-full">
                      <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.totalDays}x Days</span>

                      <div className="flex items-center gap-x-[0.125rem]">
                        <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${summaryData?.dailyRate?.toFixed(2)}</span>
                        <span
                          className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                          onClick={() => {
                            setBasePriceType("Daily");
                            setOpenBasePriceRateDialog(true);
                          }}
                        >
                          edit
                        </span>
                      </div>

                      <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${(summaryData?.totalDays * summaryData?.dailyRate)?.toFixed(2)}</span>
                    </div>
                  )
                }

                {
                  summaryData?.totalHours !== 0 && (
                    <div className="flex justify-between items-start w-full">
                      <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textHeadingColor}`}>{summaryData?.totalHours}x Hours</span>

                      <div className="flex items-center gap-x-[0.125rem]">
                        <span className={`text-xs font-medium break-all flex-1  text-right ${themeStyles.main.textHeadingColor}`}>${summaryData?.hourlyRate?.toFixed(2)}</span>
                        <span
                          className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                          onClick={() => {
                            setBasePriceType("Hourly");
                            setOpenBasePriceRateDialog(true);
                          }}
                        >
                          edit
                        </span>
                      </div>

                      <span className={`text-xs font-medium break-all flex-1 text-right ${themeStyles.main.textHeadingColor}`}>${(summaryData?.totalHours * summaryData?.hourlyRate)?.toFixed(2)}</span>
                    </div>
                  )
                }

                <Divider className={`w-full ${themeStyles.divider.bg}`} />

                {
                  summaryData?.reservationDiscountDto
                    ?.map((item: any, index: number) => {
                      return (
                        <div key={index} className="flex justify-between items-center">
                          <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{item?.discountDefinition}:</span>
                          <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>-${parseFloat(item?.discount)?.toFixed(2)}</span>
                        </div>
                      )
                    })
                }           

                {
                  summaryData?.additionalCategoryDto
                    ?.map((item: any, j: number) => {
                      return (
                        <div key={j}>
                          <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>{item?.categoryName}</span>

                          {
                            item?.additionalChargesDto?.map((value: any, i: number) => (
                              <div key={i} className="flex justify-between items-center mb-2">
                                <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{value?.name} {value?.quantity > 1 ? value?.quantity + "x " : ''}:</span>
                                <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>${(value?.quantity * value?.basePrice)?.toFixed(2)}</span>
                              </div>
                            ))
                          }
                        </div>
                      )
                    })
                }

                {
                  (
                    summaryData?.mandatoryAdditionalChargesAmount > 0
                    || summaryData?.locationFeePercentage > 0
                    || summaryData?.taxPercentage > 0
                    || summaryData?.governmentTaxPercentage > 0
                  )
                  && (
                    <div>
                      <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>Miscellaneous</span>

                      {
                        summaryData?.mandatoryAdditionalChargesAmount > 0 &&
                        (
                          <div className="flex justify-between items-center">
                            <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{summaryData?.mandatoryAdditionalChargesName}:</span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>${parseFloat(summaryData?.mandatoryAdditionalChargesAmount)?.toFixed(2)}</span>
                          </div>
                        )
                      }

                      {
                        summaryData?.locationFeePercentage > 0 &&
                        (
                          <div className="flex justify-between items-center">
                            <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{`${summaryData?.locationFeeName} (${summaryData?.locationFeePercentage}%)`}:</span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>${parseFloat(summaryData?.locationFeeAmount)?.toFixed(2)}</span>
                          </div>
                        )
                      }

                      {
                        summaryData?.taxPercentage > 0 &&
                        (
                          <div className="flex justify-between items-center">
                            <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{`${summaryData?.taxName} (${summaryData?.taxPercentage}%)`}:</span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>${parseFloat(summaryData?.taxAmount)?.toFixed(2)}</span>
                          </div>
                        )
                      }

                      {
                        summaryData?.governmentTaxPercentage > 0 &&
                        (
                          <div className="flex justify-between items-center">
                            <span className={`text-xs font-medium break-all flex-1 text-left ${themeStyles.main.textSubHeadingColor}`}>{`${summaryData?.governmentTaxName} (${summaryData?.governmentTaxPercentage}%)`}:</span>
                            <span className={`text-xs font-medium break-all w-[30%] text-right ${themeStyles.main.textHeadingColor}`}>${parseFloat(summaryData?.governmentTaxAmount)?.toFixed(2)}</span>
                          </div>
                        )
                      }
                    </div>
                  )
                }
              </div>

              <Divider className={`w-full !mt-2 ${themeStyles.divider.bg}`} />

              <div className={`flex justify-between items-center py-2`}>
                <span className={`text-sm font-bold ${themeStyles.main.textSubHeadingColor}`}>Total</span>
                <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>${summaryData?.totalPrices?.toFixed(2)}</span>
              </div>

              {
                (summaryData?.status === 'Open' || summaryData?.status === 'Quotes') && (
                  <>
                    <Divider className={`w-full ${themeStyles.divider.bg}`} />

                    <div className={`flex justify-between items-center py-4`}>
                      <span className={`text-sm font-bold ${themeStyles.main.textSubHeadingColor}`}>Security Deposit
                        <span
                          className={`material-symbols-outlined ml-1 text-[14px] cursor-pointer font-light text-[#0000ff]`}
                          onClick={() => setOpenSecurityDepositDialog(true)}
                        >
                          edit
                        </span>
                      </span>

                      <span className="text-sm font-bold text-red-500">${summaryData?.securityDepositAmount ? summaryData?.securityDepositAmount?.toFixed(2) : 0.00}</span>
                    </div>
                  </>
                )
              }

              {
                currentPage !== 0 &&
                currentPage !== 1 &&
                currentPage !== 2 && (
                  <>
                    <Divider className={`w-full ${themeStyles.divider.bg}`} />

                    <div className={`flex justify-between items-center py-2`}>
                      <span className={`text-sm font-bold ${themeStyles.main.textSubHeadingColor}`}>Outstanding Balance</span>

                      <span className={`text-sm font-bold ${themeStyles.main.textHeadingColor}`}>${summaryData?.outstandingBalance?.toFixed(2) ?? 0.00}</span>
                    </div>
                  </>
                )
              }
            </>
          )
        }
      </div>

      <SecuityDeposit
        openSecurityDepositDialog={openSecurityDepositDialog}
        setOpenSecurityDepositDialog={setOpenSecurityDepositDialog}
        reservationId={reservationId}
        securityDepositAmountToPaid={securityDepositAmount}
        updateSecurityDepositAmountToPaid={updateSecurityDepositAmountToPaid}
        setWasSecurityDepositManuallyChanged={setWasSecurityDepositManuallyChanged}
        summaryData={summaryData}
        refreshTheSummary={refreshTheSummary}
        setPleaseWaitPopup={setPleaseWaitPopup}
      />

      <BasePriceRateChange
        openBasePriceRateDialog={openBasePriceRateDialog}
        setOpenBasePriceRateDialog={setOpenBasePriceRateDialog}
        basePriceType={basePriceType}
        summaryData={summaryData}
        setPleaseWaitPopup={setPleaseWaitPopup}
        onRateChangeBeforeBookingConfirm={onRateChangeBeforeBookingConfirm}
      />
    </>
  )
}